if (!customElements.get('pdp-floating-nav')) {
  class PdpFloatingNav extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.showMode = this.dataset.showMode;
      this.dialog = this.querySelector('[data-pdp-nav-dialog]');
      this.handleClick = this.onClick.bind(this);
      this.handleDialogClose = this.onDialogClose.bind(this);
      this.handleDialogClick = (event) => {
        if (event.target === this.dialog) this.dialog.close();
      };

      this.addEventListener('click', this.handleClick);
      this.dialog?.addEventListener('close', this.handleDialogClose);
      this.dialog?.addEventListener('click', this.handleDialogClick);
      document.querySelectorAll('.rb-sticky-atc').forEach((sticky) => sticky.remove());

      this.hidden = false;
      if (this.showMode === 'always') {
        this.setVisible(true);
        return;
      }

      this.observeAddToCart();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.dialog?.removeEventListener('close', this.handleDialogClose);
      this.dialog?.removeEventListener('click', this.handleDialogClick);
      this.visibilityObserver?.disconnect();
      this.overlayListeners?.forEach(({ details, listener }) => details.removeEventListener('toggle', listener));
      this.unlockBody();
      this.initialized = false;
    }

    observeAddToCart() {
      const targets = Array.from(document.querySelectorAll(this.dataset.atcSelector));
      this.addToCart = targets.find((target) => target.offsetParent !== null) || targets[0];
      if (!this.addToCart) return;

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        const hasPassedAddToCart = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.setVisible(hasPassedAddToCart);
      });
      this.visibilityObserver.observe(this.addToCart);
    }

    setVisible(visible) {
      this.shouldBeVisible = visible;
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
    }

    onClick(event) {
      const closeButton = event.target.closest('[data-pdp-nav-close]');
      if (closeButton) {
        this.dialog?.close();
        return;
      }

      const trigger = event.target.closest('[data-pdp-nav-action]');
      if (!trigger || !this.contains(trigger)) return;

      switch (trigger.dataset.pdpNavAction) {
        case 'menu':
          event.preventDefault();
          this.openMenu();
          break;
        case 'search':
          event.preventDefault();
          this.openSearch();
          break;
        case 'cart':
          this.openCart(event, trigger);
          break;
      }
    }

    openMenu() {
      if (this.dialog) {
        this.bodyWasLocked = document.body.classList.contains('overflow-hidden');
        document.body.classList.add('overflow-hidden');
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
        return;
      }

      const headerMenu = document.getElementById('Details-menu-drawer-container');
      if (headerMenu) this.openDetails(headerMenu);
    }

    openSearch() {
      const searches = Array.from(document.querySelectorAll('details-modal.header__search > details'));
      const search = searches.find((details) => details.offsetParent !== null) || searches[0];
      if (search) {
        this.openDetails(search);
      } else {
        window.location.href = this.dataset.searchUrl;
      }
    }

    openCart(event, trigger) {
      const headerCart = document.getElementById('cart-icon-bubble');
      if (!headerCart || headerCart === trigger) return;
      event.preventDefault();
      headerCart.click();
    }

    openDetails(details) {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      const listener = () => this.classList.toggle('is-suppressed', details.open);
      this.overlayListeners = this.overlayListeners || [];
      if (!this.overlayListeners.some((item) => item.details === details)) {
        details.addEventListener('toggle', listener);
        this.overlayListeners.push({ details, listener });
      }
      summary.click();
      listener();
    }

    onDialogClose() {
      this.unlockBody();
    }

    unlockBody() {
      if (this.bodyWasLocked === false) document.body.classList.remove('overflow-hidden');
      this.bodyWasLocked = undefined;
    }
  }

  customElements.define('pdp-floating-nav', PdpFloatingNav);
}

if (!customElements.get('pdp-floating-atc')) {
  class PdpFloatingAtc extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.showMode = this.dataset.showMode;
      this.button = this.querySelector('[data-pdp-atc-submit]');
      this.buttonLabel = this.querySelector('[data-pdp-atc-button-label]');
      this.subtitle = this.querySelector('[data-pdp-atc-subtitle]');
      this.price = this.querySelector('[data-pdp-atc-price]');
      this.image = this.querySelector('[data-pdp-atc-image], .pdp-floating-atc__image');
      this.handleClick = this.onClick.bind(this);
      this.handleVariantChange = this.onVariantChange.bind(this);
      this.handleResize = this.updateNavigationOffset.bind(this);

      this.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.hidden = false;
      this.locateProductElements();
      this.observeNavigation();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }

      if (this.showMode === 'always') this.setVisible(true);
      else this.observeAddToCart();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);
      this.visibilityObserver?.disconnect();
      this.buttonObserver?.disconnect();
      this.navigationObserver?.disconnect();
      this.navigationResizeObserver?.disconnect();
      this.variantUnsubscriber?.();
      this.initialized = false;
    }

    locateProductElements() {
      const productInfos = Array.from(document.querySelectorAll('product-info')).filter(
        (element) => !element.closest('quick-add-modal')
      );
      this.productInfo = productInfos.find((element) => element.offsetParent !== null) || productInfos[0];
      this.addToCart = this.productInfo?.querySelector('.product-form__buttons');
      this.mainButton = this.productInfo?.querySelector('product-form [type="submit"][name="add"]');
      this.observeMainButton();
      this.syncButtonState();
    }

    observeAddToCart() {
      this.visibilityObserver?.disconnect();
      if (!this.addToCart) return;

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        const isPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        const shouldShow = this.showMode === 'when_hidden' ? !entry.isIntersecting : isPast;
        this.setVisible(shouldShow);
      });
      this.visibilityObserver.observe(this.addToCart);
    }

    observeMainButton() {
      this.buttonObserver?.disconnect();
      if (!this.mainButton) return;

      this.buttonObserver = new MutationObserver(() => this.syncButtonState());
      this.buttonObserver.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    observeNavigation() {
      this.navigation = document.querySelector('pdp-floating-nav');
      if (!this.navigation) {
        this.updateNavigationOffset();
        return;
      }

      this.navigationObserver = new MutationObserver(() => this.updateNavigationOffset());
      this.navigationObserver.observe(this.navigation, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style'],
      });

      if (typeof ResizeObserver === 'function') {
        this.navigationResizeObserver = new ResizeObserver(() => this.updateNavigationOffset());
        this.navigationResizeObserver.observe(this.navigation);
      }
      this.updateNavigationOffset();
    }

    updateNavigationOffset() {
      const navigation = this.navigation;
      const isVisible =
        navigation &&
        !navigation.hidden &&
        navigation.offsetHeight > 0 &&
        navigation.classList.contains('is-visible') &&
        !navigation.classList.contains('is-suppressed');
      let offset = 0;

      if (isVisible) {
        const navigationBottom = Number.parseFloat(getComputedStyle(navigation).bottom) || 0;
        offset = navigation.offsetHeight + navigationBottom;
      }
      this.style.setProperty('--pdp-atc-nav-offset', `${Math.round(offset)}px`);
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
      this.updateNavigationOffset();
    }

    onClick(event) {
      if (!event.target.closest('[data-pdp-atc-submit]')) return;
      if (!this.mainButton?.isConnected) this.locateProductElements();
      this.syncButtonState();
      if (!this.mainButton || this.button.disabled) return;

      this.mainButton.click();
      requestAnimationFrame(() => this.syncButtonState());
    }

    syncButtonState() {
      if (!this.button) return;
      const unavailable =
        !this.mainButton ||
        this.mainButton.disabled ||
        this.mainButton.getAttribute('aria-disabled') === 'true';
      const loading = this.mainButton?.classList.contains('loading') || false;
      const mainLabel = Array.from(this.mainButton?.querySelectorAll('span') || []).find(
        (element) =>
          !element.classList.contains('loading__spinner') &&
          !element.classList.contains('hidden') &&
          element.textContent.trim()
      );

      this.button.disabled = unavailable;
      this.button.setAttribute('aria-disabled', String(unavailable));
      this.button.classList.toggle('loading', loading);
      this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      if (this.buttonLabel) {
        this.buttonLabel.textContent = unavailable
          ? mainLabel?.textContent.trim() || this.dataset.soldLabel
          : this.dataset.addLabel;
      }
    }

    onVariantChange({ data }) {
      if (!data?.variant) return;
      const productSection = this.productInfo?.dataset.section;
      if (productSection && data.sectionId && productSection !== String(data.sectionId)) return;

      if (this.dataset.subtitleSource === 'variant' && this.subtitle) {
        const subtitle = data.variant.title === 'Default Title' ? '' : data.variant.title;
        this.subtitle.textContent = subtitle;
        this.subtitle.classList.toggle('hidden', !subtitle);
      }

      this.updatePrice(data.html, data.sectionId);
      this.updateImage(data.variant);
      requestAnimationFrame(() => {
        this.locateProductElements();
        if (this.showMode !== 'always') this.observeAddToCart();
      });
    }

    updatePrice(html, sectionId) {
      if (!this.price || !html) return;
      const priceRoot = html.getElementById?.(`price-${sectionId}`) || html.querySelector?.('[id^="price-"]');
      const priceNode =
        priceRoot?.querySelector('.price--on-sale .price-item--sale') ||
        priceRoot?.querySelector('.price__regular .price-item--regular') ||
        priceRoot?.querySelector('.price-item');
      const priceText = priceNode?.textContent.trim();
      if (priceText) this.price.textContent = priceText;
    }

    updateImage(variant) {
      if (!this.image || this.dataset.useVariantImage !== 'true') return;
      const preview = variant.featured_media?.preview_image;
      if (!preview?.src) return;

      const imageUrl = new URL(preview.src, window.location.origin);
      imageUrl.searchParams.set('width', '240');
      this.image.removeAttribute('srcset');
      this.image.src = imageUrl.toString();
      if (preview.alt) this.image.alt = preview.alt;
    }
  }

  customElements.define('pdp-floating-atc', PdpFloatingAtc);
}
